import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";

const SettingsCtx = createContext({ settings: null, refresh: () => {} });

const isFilled = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.some(isFilled);
  if (typeof v === "object") return Object.values(v).some(isFilled);
  return true;
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch {
      setSettings({});
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Inject dynamic <head> pieces when settings change
  useEffect(() => {
    if (!settings) return;
    // Note: document.title is owned by useSEO on each page (so per-page titles win).
    const brand = settings.business_name || "BOLAWPRESS";
    if (!document.title || document.title === "") {
      document.title = settings.seo_title || brand;
    }
    // Meta description
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", settings.seo_description);
    if (settings.google_search_console_verification) {
      setMeta("google-site-verification", settings.google_search_console_verification);
    }
    // Favicon
    if (settings.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = settings.favicon_url;
    }
    // Google Analytics 4
    if (settings.google_analytics_id && !document.getElementById("ga4-script")) {
      const s = document.createElement("script");
      s.id = "ga4-script"; s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${settings.google_analytics_id}`;
      document.head.appendChild(s);
      const inline = document.createElement("script");
      inline.id = "ga4-inline";
      inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${settings.google_analytics_id}');`;
      document.head.appendChild(inline);
    }
    // Meta Pixel
    if (settings.meta_pixel_id && !document.getElementById("meta-pixel-script")) {
      const s = document.createElement("script");
      s.id = "meta-pixel-script";
      s.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${settings.meta_pixel_id}');fbq('track','PageView');`;
      document.head.appendChild(s);
    }

    // Site-wide Organization + WebSite JSON-LD (kept in sync with settings)
    const origin = window.location.origin;
    const socialSameAs = Object.values(settings.social_links || {}).filter(Boolean);
    const orgLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand,
      url: origin,
      ...(settings.logo_url ? { logo: settings.logo_url } : {}),
      ...(socialSameAs.length ? { sameAs: socialSameAs } : {}),
      ...(settings.contact_emails?.length || settings.phone_numbers?.length ? {
        contactPoint: [{
          "@type": "ContactPoint",
          contactType: "customer service",
          ...(settings.contact_emails?.[0] ? { email: settings.contact_emails[0] } : {}),
          ...(settings.phone_numbers?.[0] ? { telephone: settings.phone_numbers[0] } : {}),
        }],
      } : {}),
      ...(settings.physical_address ? {
        address: { "@type": "PostalAddress", streetAddress: settings.physical_address },
      } : {}),
    };
    const siteLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: brand,
      url: origin,
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/shop?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
    const putLd = (id, obj) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.id = id; el.type = "application/ld+json";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(obj);
    };
    putLd("ld-organization", orgLd);
    putLd("ld-website", siteLd);
  }, [settings]);

  const value = useMemo(() => ({ settings, refresh, isFilled }), [settings, refresh]);
  return <SettingsCtx.Provider value={value}>{children}</SettingsCtx.Provider>;
}

export const useSettings = () => useContext(SettingsCtx);
export { isFilled };
